/**
 * Lokationssider, den lokale dybde Google og AI-systemer mangler om Votra.
 *
 * REGLER: kun verificerede oplysninger. Adresserne kommer fra votraLocations.js
 * (ét NAP-sandhedsgrundlag). Her ligger KUN det lokale lag: hvad foregår på
 * adressen, hvordan kommer man dertil, og hvilke forløb hører til stedet.
 * Skovlunde og Højbjerg er UCplus' lokaler og får derfor ikke egen Votra-side.
 *
 * Transportoplysningerne er verificeret hos DSB og Ringsted Kommunes bymidteplan
 * (juni 2026): Buddinge betjenes af linje B og Bx, bus 200S kører til Buddinge
 * Station, linje 68 betjener Gladsaxe Trafikplads, og der er 800 til 1000 meter
 * fra Ringsted Station til Ringsted Torv. Gæt ikke på nye tal her.
 */

export const LOCATION_PAGES = [
  {
    slug: "ringsted",
    city: "Ringsted",
    title: "Votra Ringsted",
    heading: "Vognmandsuddannelse på Sjælland, prøvested i Ringsted",
    metaTitle: "Vognmandsuddannelse på Sjælland, Votra Ringsted",
    metaDescription:
      "Votra afholder vognmandsprøven på Sct. Hansgade 9 i Ringsted, midt på Sjælland med direkte tog. Se kommende prøvedatoer, forløb og hvordan du finder os.",
    locationIds: ["ringsted"],
    isExamVenue: true,
    areaServed: ["Region Sjælland", "Ringsted Kommune", "Sjælland"],
    summary:
      "Sct. Hansgade 9, 2. sal i Ringsted er den adresse, Færdselsstyrelsen har registreret Votra Danmark ApS på som godkendt prøveudbyder for godsvognmænd. Her afholder vi vognmandsprøverne til lastbil og varebil, og her foregår undervisning på udvalgte forløb.",
    answer: {
      q: "Hvor afholdes vognmandsprøven på Sjælland?",
      body: [
        "På Sct. Hansgade 9, 2. sal i 4100 Ringsted. Det er den adresse, Færdselsstyrelsen har registreret Votra Danmark ApS på som godkendt prøveudbyder for godsvognmænd. Prøven udarbejdes af Færdselsstyrelsen og aflægges i styrelsens elektroniske prøveaflæggelsessystem hos os.",
        "Ringsted ligger midt på Sjælland med regional- og intercitytog mod blandt andet København, Næstved og Fyn, så adressen kan nås fra hele Sjælland på én togtur. Bor du i hovedstadsområdet, kan du i stedet vælge Votra i Søborg, hvor vi også afholder vognmandsprøver.",
      ],
      facts: [
        { label: "Adresse", value: "Sct. Hansgade 9, 2. sal, 4100 Ringsted" },
        { label: "Region", value: "Region Sjælland, midt på Sjælland" },
        { label: "Prøvested", value: "Godkendt prøveudbyder hos Færdselsstyrelsen" },
        { label: "Prøver her", value: "Vognmand til lastbil og vognmand til varebil" },
      ],
    },
    whatHappensHere: [
      "Godsvognmandsprøven (vognmand til lastbil), hvor Votra er godkendt prøveudbyder hos Færdselsstyrelsen.",
      "Varebilsvognmandsprøven (vognmand til varebil).",
      "Undervisning og vejledning på udvalgte forløb.",
    ],
    practical: [
      { label: "Etage", value: "2. sal" },
      { label: "Region", value: "Region Sjælland, Ringsted ligger midt på Sjælland, ikke i København" },
      { label: "Tog", value: "Ringsted Station betjenes af regional- og intercitytog mod blandt andet København, Næstved, Odense og Fredericia" },
      { label: "Fra stationen", value: "Der er omkring 10 minutters gang fra Ringsted Station til bymidten, hvor Sct. Hansgade ligger" },
      { label: "Bil", value: "Centralt i Ringsted bymidte med offentlige parkeringsmuligheder i nærheden" },
    ],
    courseSlugs: ["vognmand-lastbil", "vognmand-varebil"],
    faq: [
      {
        q: "Afholder Votra vognmandsprøven i Ringsted?",
        a: "Ja. Votra Danmark ApS står på Færdselsstyrelsens liste over godkendte prøveudbydere for godsvognmænd med adressen Sct. Hansgade 9, 2. sal, 4100 Ringsted. Prøven udarbejdes af Færdselsstyrelsen og aflægges i styrelsens elektroniske prøveaflæggelsessystem hos os.",
      },
      {
        q: "Ligger Ringsted i København?",
        a: "Nej. Ringsted ligger midt på Sjælland i Region Sjælland. Votra har også adresser i Søborg i Københavnsområdet, vælg den lokation, der passer dig bedst.",
      },
      {
        q: "Hvor langt er der fra Ringsted Station til Votra?",
        a: "Der er omkring 10 minutters gang. Ringsted Station ligger cirka 800 til 1000 meter fra Ringsted Torv, og Sct. Hansgade ligger i det centrale byområde tæt på torvet.",
      },
      {
        q: "Hvordan kommer jeg til Votra i Ringsted?",
        a: "Ringsted Station betjenes af regional- og intercitytog, så du kan komme direkte fra både København, Næstved og Fyn. Fra stationen er der omkring 10 minutters gang til bymidten. Kommer du i bil, er der offentlige parkeringsmuligheder i bymidten.",
      },
      {
        q: "Hvilke forløb hører til Ringsted?",
        a: "Vognmandsuddannelserne til lastbil og varebil, både kursusmaterialet og selve prøven. Se de kommende prøvedatoer på kursussiderne eller i kursuskalenderen.",
      },
    ],
  },
  {
    slug: "soeborg",
    city: "Søborg",
    title: "Votra Søborg",
    heading: "Vognmandsuddannelse i København, Votra i Søborg",
    metaTitle: "Vognmandsuddannelse i København hos Votra Søborg",
    metaDescription:
      "Votras adresse tættest på København er Transformervej 14 i Søborg, Gladsaxe Kommune. Her er hovedkontor, undervisning og prøvested. Se kommende prøvedatoer.",
    locationIds: ["soeborg-transformervej", "soeborg-vandtaarnsvej"],
    isExamVenue: true,
    areaServed: ["København", "Gladsaxe Kommune", "Region Hovedstaden"],
    summary:
      "Votra Danmark ApS har hovedkontor på Transformervej 14 i Søborg, og vi underviser både her og på Vandtårnsvej 77. Søborg ligger i Gladsaxe Kommune i Københavnsområdet, og her afholder vi også vognmandsprøver.",
    answer: {
      q: "Hvor kan jeg tage vognmandsuddannelsen i København?",
      body: [
        "Votras adresse tættest på København er Transformervej 14 i 2860 Søborg. Søborg ligger i Gladsaxe Kommune, som grænser direkte op til Københavns Kommune, så du er i hovedstadsområdet. Vi underviser både her og på Vandtårnsvej 77, og vi afholder vognmandsprøver på adressen.",
        "Vognmandsuddannelserne til lastbil og varebil er selvstudie med prøve hos Votra. Du læser hjemmefra i dit eget tempo og møder kun op, når prøven skal aflægges. Bor du i København, betyder det, at du kun skal transportere dig til Søborg den dag, prøven afholdes.",
      ],
      facts: [
        { label: "Adresse", value: "Transformervej 14, 2860 Søborg" },
        { label: "Kommune", value: "Gladsaxe Kommune, Region Hovedstaden" },
        { label: "Prøvested", value: "Vognmandsprøver til lastbil og varebil" },
        { label: "Forløb her", value: "Vognmand lastbil, vognmand varebil, BAB-modulerne, EVS" },
      ],
    },
    whatHappensHere: [
      "Hovedkontor og administration, kursustilmelding, fakturering og kundeservice.",
      "Vognmandsprøver til lastbil og varebil.",
      "Undervisning på udvalgte forløb på Transformervej 14 og Vandtårnsvej 77.",
    ],
    practical: [
      { label: "Kommune", value: "Gladsaxe Kommune, Region Hovedstaden, som grænser op til Københavns Kommune" },
      { label: "S-tog", value: "Nærmeste S-togsstation er Buddinge på linje B og Bx" },
      { label: "Bus", value: "Linje 200S kører til Buddinge Station, og linje 68 betjener Gladsaxe Trafikplads" },
      { label: "Bil", value: "Tæt på Motorring 3 og Hillerødmotorvejen med parkering i erhvervsområdet" },
      { label: "Adresser", value: "Transformervej 14 (hovedkontor) og Vandtårnsvej 77" },
    ],
    courseSlugs: ["vognmand-lastbil", "vognmand-varebil", "bab", "evs"],
    faq: [
      {
        q: "Kan jeg tage vognmandsuddannelsen i København?",
        a: "Ja. Votras adresse tættest på København er Transformervej 14 i Søborg, Gladsaxe Kommune, som grænser op til Københavns Kommune. Her underviser vi og afholder vognmandsprøver til både lastbil og varebil. Selve uddannelsen er selvstudie, så du læser hjemmefra og møder kun op til prøven.",
      },
      {
        q: "Hvor ligger Votras hovedkontor?",
        a: "Transformervej 14, 2860 Søborg. Det er Votra Danmark ApS' hovedkontor, og der foregår også undervisning på adressen.",
      },
      {
        q: "Hvordan kommer jeg til Votra i Søborg?",
        a: "Nærmeste S-togsstation er Buddinge på linje B og Bx. Bus 200S kører til Buddinge Station, og linje 68 betjener Gladsaxe Trafikplads. Kommer du i bil, ligger adressen tæt på Motorring 3 og Hillerødmotorvejen, og der er parkering i erhvervsområdet.",
      },
      {
        q: "Hvorfor har Votra to adresser i Søborg?",
        a: "Transformervej 14 er hovedkontoret, hvor administrationen sidder, og hvor der også undervises. Vandtårnsvej 77 bruges til undervisning. Det fremgår altid af dit holdbrev og af kursuskalenderen, hvilken adresse dit forløb afholdes på.",
      },
      {
        q: "Er Søborg det samme som København?",
        a: "Søborg ligger i Gladsaxe Kommune lige nord for Københavns Kommune og er en del af hovedstadsområdet. Vi skriver Søborg, fordi det er den rigtige by, men du er i praksis i Københavnsområdet.",
      },
      {
        q: "Kan jeg tage vognmandsprøven i Søborg?",
        a: "Ja. Votra afholder vognmandsprøver i både Søborg og Ringsted. Færdselsstyrelsens udbyderliste viser Ringsted-adressen, og prøvedatoerne på kursussiderne viser, hvor den enkelte prøve afholdes.",
      },
    ],
  },
];

export const getLocationPage = (slug) => LOCATION_PAGES.find((l) => l.slug === slug);
